import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import FormSection from '../../components/admin/FormSection';
import { LoadingState } from '../../components/admin/StateViews';
import MultiImageManager from '../../components/MultiImageManager';
import { ImpactStory } from '../../types';
import { ImpactStoryService } from '../../services/ImpactStoryService';
import { useToast } from '../../context/ToastContext';

type StoryErrors = Partial<Record<'title' | 'story' | 'images', string>>;

const ImpactStoryFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const isEditMode = Boolean(id);
  const [story, setStory] = useState<Partial<ImpactStory>>({
    title: '',
    story: '',
    images: [],
  });
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<StoryErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchStory = async () => {
        try {
          const data = await ImpactStoryService.getImpactStoryById(id);
          setStory({ ...data, images: data.images || [] });
        } catch (loadError) {
          setError('Failed to fetch story details.');
          console.error(loadError);
        } finally {
          setLoading(false);
        }
      };
      fetchStory();
    }
  }, [id, isEditMode]);

  const imageUrls = useMemo(() => story.images || [], [story.images]);

  const validate = (): StoryErrors => {
    const nextErrors: StoryErrors = {};

    if (!story.title?.trim()) {
      nextErrors.title = 'Title is required.';
    }

    if (!story.story?.trim()) {
      nextErrors.story = 'Content is required.';
    }

    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setError('Please resolve the highlighted fields before saving.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const storyData = {
      title: story.title || '',
      story: story.story || '',
      images: story.images?.filter((url) => url.trim() !== '') || [],
    };

    try {
      if (isEditMode && id) {
        await ImpactStoryService.updateImpactStory(id, storyData);
      } else {
        await ImpactStoryService.createImpactStory(storyData);
      }
      showToast(isEditMode ? 'Impact story updated.' : 'Impact story created.');
      navigate('/content/stories');
    } catch (submitError) {
      setError(
        isEditMode ? 'Failed to update story.' : 'Failed to create story.'
      );
      console.error(submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading impact story form…" />;
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 4 } }}>
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <FormSection
            title={isEditMode ? 'Edit impact story' : 'Create impact story'}
            description="This keeps the same local-state form structure used across the admin app while reusing the shared layout and media manager."
          >
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                required
                value={story.title}
                onChange={(event) =>
                  setStory((current) => ({ ...current, title: event.target.value }))
                }
                error={Boolean(errors.title)}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <MultiImageManager
                imageUrls={imageUrls}
                onImageUrlsChange={(newUrls) =>
                  setStory((current) => ({ ...current, images: newUrls }))
                }
                isReadOnly={false}
                uploadCategory="story-image"
                helperText="Use the same multi-image pattern as the Causes form for consistency."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Content"
                fullWidth
                required
                multiline
                rows={10}
                value={story.story}
                onChange={(event) =>
                  setStory((current) => ({ ...current, story: event.target.value }))
                }
                error={Boolean(errors.story)}
                helperText={errors.story}
              />
            </Grid>
          </FormSection>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/content/stories')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : isEditMode ? (
                'Save Changes'
              ) : (
                'Create Story'
              )}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ImpactStoryFormPage;
